import React, { Component } from 'react';
import { array, arrayOf, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import { FilterPopup, FilterPlain } from '../../components';
import css from './SingleRangeFilter.css';
import { Field, FormSpy } from 'react-final-form';
import debounce from 'lodash/debounce';


const DEBOUNCE_WAIT_TIME = 400;
const functionDisable = () => false;

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

const RangeSlider = props => {
  const { name, id, label, min, max, step, labelValueId, liveEdit } = props;

  return (
    <Field 
      name={name}
      id={id}
      label={label}
      render={fieldRenderProps => {
        const { input } = fieldRenderProps;
        const { value, onChange } = input;

        const defaultValue = value || min;
        const inputProps = {
          min,
          max,
          step,
          onChange,
          value: defaultValue,
        }

        return (
          <div className={css.sliderWrapper}>
            <div className={css.sliderLabel}>
              <span>{label}</span>
              <span>
                <FormattedMessage id="DistanceFilter.labelValue" values={{value: defaultValue}}/>
              </span>
            </div>
            <div className={css.range}>
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                      #range-slide-${name}::-webkit-slider-runnable-track {
                        background:linear-gradient(to right, #c0392b 0%, #c0392b ${defaultValue}%, #b2b2b2 ${defaultValue}%);
                      }

                      #range-slide-${name}::-moz-range-track {
                        background:linear-gradient(to right, #c0392b 0%, #c0392b ${defaultValue}%, #b2b2b2 ${defaultValue}%);
                      }
                    `,
                }}
              />
              <input
                id={`range-slide-${name}`}
                className={css.slider}
                type="range"
                {...inputProps}
              />
            </div>
          </div>
        );
      }}
    />
  );
};

class SingleRangeFilter extends Component {
  constructor(props) {
    super(props);

    this.filter = null;
    this.filterContent = null;

    this.positionStyleForContent = this.positionStyleForContent.bind(this);
  }

  positionStyleForContent() {
    if (this.filter && this.filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - this.filter.getBoundingClientRect().right;
      const labelWidth = this.filter.offsetWidth;
      const contentWidth = this.filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const contentPlacementOffset = this.props.contentPlacementOffset;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      name,
      label,
      options,
      initialValues,
      contentPlacementOffset,
      onSubmit,
      queryParamNames,
      searchMode,
      intl,
      showAsPopup,
      labelSelectedId,
      labelValueId,
      min,
      max,
      step,
      ...rest
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const queryParamName = getQueryParamName(queryParamNames);
    const initialValue = initialValues && initialValues[queryParamName] ? initialValues[queryParamName] : null;

    const labelForPopup = initialValue
      ? intl.formatMessage(
          { id: labelSelectedId },
          { value: initialValue }
        )
      : label;

    const labelForPlain = initialValue
      ? intl.formatMessage(
          { id: labelSelectedId },
          { value: initialValue }
        )
      : label;

    const contentStyle = this.positionStyleForContent();

    const handleSubmit = values => {
      const usedValue = values && values[name] ? Number(values[name]) : null;
      onSubmit({ [queryParamName]: usedValue });
    };

    const handleChange = debounce(
      formState => {
        if (formState.dirty) {
          const values = formState.values;
          const usedValue = values && values[name] ? Number(values[name]) : null;
          onSubmit({ [queryParamName]: usedValue });
        }
      },
      DEBOUNCE_WAIT_TIME,
      { leading: false, trailing: true }
    );

    return showAsPopup ? (
      <FilterPopup
        className={classes}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        name={name}
        label={labelForPopup}
        isSelected={!!initialValue}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        initialValues={initialValue ? initialValues : { [name]: min }}
        keepDirtyOnReinitialize
        {...rest}
      >
        <RangeSlider
          name={name}
          id={name}
          label={label}
          labelValueId={labelValueId}
          min={min}
          max={max}
          step={step}
        />
      </FilterPopup>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={labelForPlain}
        isSelected={!!initialValue}
        id={`${id}.plain`}
        liveEdit
        contentPlacementOffset={contentStyle}
        onSubmit={functionDisable}
        initialValues={initialValue ? initialValues : { [name]: min }}
        onClear={handleSubmit}
        {...rest}
      >
        <FormSpy onChange={handleChange} subscription={{ values: true, dirty: true }} />
        <RangeSlider
          name={name}
          id={name}
          label={label}
          labelValueId={labelValueId}
          min={min}
          max={max}
          step={step}
        />
      </FilterPlain>
    );
  }
}

SingleRangeFilter.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  contentPlacementOffset: 0,
  min: 0,
  max: 100,
  step: 1,
};

SingleRangeFilter.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  name: string.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSubmit: func.isRequired,
  initialValues: object,
  contentPlacementOffset: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SingleRangeFilter);
